import React, { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useGetProductsByGiftCategoryQuery } from '../slices/productsApiSlice';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Products from '../components/Products';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';

/**
 * ============================================
 * CHANGES MADE:
 * 1. REMOVED: Gradient background section (lines 140-151)
 * 2. REMOVED: Gradient overlay (rgba with position absolute)
 * 3. REPLACED: With simple clean header (no background)
 * 4. KEPT: All functionality and filters intact
 * 5. ADDED: Responsive gift icon size (3rem mobile, 5rem desktop)
 * ============================================
 */

const GiftCategoryPage = () => {
    const { category } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [category]);

    // Fetch products for this gift category
    const { data: products, isLoading, error } = useGetProductsByGiftCategoryQuery(category);

    // Get filter values from URL
    const productTypeFilters = searchParams.getAll('productType');
    const minPrice = searchParams.get('minPrice');
    const maxPrice = searchParams.get('maxPrice');
    const inStock = searchParams.get('inStock') === 'true';
    const onSale = searchParams.get('onSale') === 'true';
    const colorFilters = searchParams.getAll('color');
    const characterFilters = searchParams.getAll('character');

    // Apply filters to products
    const getFilteredProducts = () => {
        if (!products) return [];

        let filtered = [...products];

        // Filter by product type
        if (productTypeFilters.length > 0) {
            filtered = filtered.filter(p => productTypeFilters.includes(p.productType));
        }

        // Filter by price
        if (minPrice) {
            filtered = filtered.filter(p => p.price >= parseFloat(minPrice));
        }
        if (maxPrice) {
            filtered = filtered.filter(p => p.price <= parseFloat(maxPrice));
        }

        // Filter by stock
        if (inStock) {
            filtered = filtered.filter(p => p.countInStock > 0);
        }

        // Filter by sale
        if (onSale) {
            filtered = filtered.filter(p => p.isClearance === true);
        }

        // Filter by color
        if (colorFilters.length > 0) {
            filtered = filtered.filter(p =>
                p.colour && p.colour.some(c => colorFilters.includes(c))
            );
        }

        // Filter by character
        if (characterFilters.length > 0) {
            filtered = filtered.filter(p => characterFilters.includes(p.character));
        }

        return filtered;
    };

    const filteredProducts = getFilteredProducts();
    const hasActiveFilters = productTypeFilters.length > 0 || minPrice || maxPrice || inStock || onSale || colorFilters.length > 0 || characterFilters.length > 0;

    // Map slugs to display names
    const categoryTitles = {
        'gamers': 'Gifts for Gamers',
        'movie-buffs': 'Gifts for Movie Buffs',
        'tv-fans': 'Gifts for TV Fans',
        'superhero': 'Gifts for a Superhero',
        'kids': 'Gifts for Kids',
        'dad': 'Gifts for Dad',
        'retro-lovers': 'Gifts for Retro Lovers'
    };

    const categoryDescriptions = {
        'gamers': 'Perfect for gaming enthusiasts who love classic and modern video games',
        'movie-buffs': 'For cinema lovers who appreciate the magic of the silver screen',
        'tv-fans': 'Binge-worthy presents for TV show superfans',
        'superhero': 'Heroic gift ideas for fans of Marvel, DC, and more',
        'kids': 'Fun and nostalgic gifts that little ones will love',
        'dad': 'Perfect gifts for Father\'s Day or just to show you care',
        'retro-lovers': 'Nostalgic classics that brings back the best memories'
    };

    // const categoryIcons = {
    //     'gamers': '🎮',
    //     'movie-buffs': '🎬',
    //     'tv-fans': '📺',
    //     'superhero': '🦸',
    //     'kids': '🧸',
    //     'dad': '👔',
    //     'retro-lovers': '🕹️'
    // };

    const categoryIcons = {
        'gamers': '/images/icons/si.png',
        'movie-buffs': '/images/icons/jaws.png',
        'tv-fans': '/images/icons/Ateam.png',
        'superhero': '/images/icons/batman.png',
        'kids': '/images/icons/turtles.png',
        'dad': '/images/icons/bttf.png',
        'retro-lovers': '/images/icons/pacman.png'
    };

    const pageTitle = categoryTitles[category] || 'Gift Category';
    const pageDescription = categoryDescriptions[category] || 'Discover our curated selection';
    const pageIcon = categoryIcons[category] || '🎁';

    return (
        <>
            <style>
                {`
                    .gift-icon {
                        font-size: 5rem;
                        margin-bottom: 0rem;
                    }

                    @media (max-width: 768px) {
                        .gift-icon {
                            font-size: 3rem;
                        }
                    }
                `}
            </style>

            <div style={{ marginTop: window.innerWidth < 768 ? '-3rem' : '0' }}>
                {/* Back Button */}
                <Link to="/" className="btn btn-light my-3 orange-outline">
                    Go Back
                </Link>

                {/* ============================================
                    CHANGED: Replaced gradient section with clean header
                    OLD: Full-width gradient with overlay (lines 140-174)
                    NEW: Simple centered header (no background)
                    ============================================ */}
                <Container style={{
                    textAlign: 'center',
                    marginTop: '-2rem',
                    marginBottom: '3rem'
                }}>
                    {/*<div className="gift-icon">*/}
                    {/*    {pageIcon}*/}
                    {/*</div>*/}
                    <div className="gift-icon">
                        {pageIcon.startsWith('/images') ? (
                            <img
                                src={pageIcon}
                                alt={pageTitle}
                                style={{ width: '200px', height: '200px', objectFit: 'contain' }}
                            />
                        ) : (
                            pageIcon
                        )}
                    </div>


                    <h1 style={{
                        fontWeight: 'bold',
                        marginBottom: '1rem',
                        color: '#3E6D9C'
                    }}>
                        {pageTitle}
                    </h1>
                    <p style={{
                        marginBottom: 0,
                        color: '#666',
                        fontSize: '1.1rem',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        {pageDescription}
                    </p>
                </Container>

                {/* Products Grid Section - UNCHANGED */}
                <Container style={{ marginTop: '3rem', marginBottom: '3rem' }}>
                    {isLoading ? (
                        <Loader />
                    ) : error ? (
                        <Message variant="danger">
                            {error?.data?.message || error.error || 'Failed to load products'}
                        </Message>
                    ) : products && products.length > 0 ? (
                        <>
                            <div style={{
                                textAlign: 'center',
                                marginBottom: '2rem',
                                padding: '1rem',
                                background: '#f8f9fa',
                                borderRadius: '10px'
                            }}>
                                <h3 style={{ color: '#3E6D9C', marginBottom: '0.5rem' }}>
                                    {filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'} Found
                                    {hasActiveFilters && ` (${products.length} total)`}
                                </h3>
                                <p style={{ color: '#666', margin: 0, fontWeight: 'bold' }}>
                                    Handpicked for {categoryTitles[category]?.toLowerCase().replace('gifts for ', '') || 'this category'}
                                </p>
                            </div>

                            {filteredProducts.length > 0 ? (
                                <Row>
                                    {filteredProducts.map((product) => (
                                        <Col key={product._id} xs={6} sm={6} md={4} lg={3} className="mb-4">
                                            <Products product={product} showDiscount={product.isClearance} />
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '3rem 2rem',
                                    background: '#f8f9fa',
                                    borderRadius: '15px'
                                }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                                    <h3 style={{ color: '#3E6D9C', marginBottom: '1rem' }}>
                                        No products match your filters
                                    </h3>
                                    <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                                        Try adjusting your filters to see more results
                                    </p>
                                    <Button
                                        className="orange-outline"
                                        onClick={() => navigate(`/gifts/${category}`)}
                                    >
                                        Clear All Filters
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '4rem 2rem',
                            background: '#f8f9fa',
                            borderRadius: '15px'
                        }}>
                            <div style={{ fontSize: '5rem', marginBottom: '1.5rem' }}>
                                😢
                            </div>
                            <h3 style={{ color: '#3E6D9C', marginBottom: '1rem' }}>
                                No Products Found
                            </h3>
                            <p style={{ color: '#666', marginBottom: '2rem' }}>
                                We don't have any products tagged for this category yet.
                                <br />
                                Check back soon or browse all our products!
                            </p>
                            <Button
                                className="orange-outline"
                                onClick={() => navigate('/search')}
                            >
                                Browse All Products
                            </Button>
                        </div>
                    )}

                    {/* Related Gift Categories - UNCHANGED */}
                    {products && products.length > 0 && (
                        <div style={{
                            marginTop: '4rem',
                            padding: '2rem',
                            background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)',
                            borderRadius: '15px',
                            textAlign: 'center'
                        }}>
                            <h4 style={{ color: '#3E6D9C', marginBottom: '1.5rem' }}>
                                Looking for something else?
                            </h4>
                            <Button
                                className="blue-outline me-3"
                                onClick={() => navigate('/')}
                            >
                                <FontAwesomeIcon icon={faGift} style={{ marginRight: '0.5rem' }} />
                                Back to Gift Finder
                            </Button>
                            <Button
                                className="orange-outline"
                                onClick={() => navigate('/search')}
                            >
                                Browse All Products
                            </Button>
                        </div>
                    )}
                </Container>
            </div>
        </>
    );
};

export default GiftCategoryPage;